/* ============================================================================
   WiseQuest — Apple-design motion refinement layer (interactions)
   ADDITIVE + dependency-free. Coordinates with wisequest.js (does not rebind
   its click handlers): the accordion observes the .is-open class that
   wisequest.js already toggles, rather than binding its own click.

     1. Spring engine   — Apple's damping + response (not fixed durations)
     2. Scroll reveal   — IntersectionObserver, inline styles cleared on settle
     3. Spring accordion— .faq__a height, driven off .is-open via MutationObserver
     4. Header material — .wq-scrolled on scroll (no transform → sticky-safe)
     5. Hero load-in    — staged rise of hero/phero copy on first paint
     6. Cart drawer     — drag-to-dismiss on the stock <cart-drawer> (§2,5,6,9)

   Reduced-motion: every auto-motion is skipped here AND neutralized in CSS.
   Re-inits on shopify:section:load; all binds are idempotent (dataset guards).
   ============================================================================ */
(function () {
  'use strict';

  var reduceMQ = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  function reduced() { return reduceMQ.matches; }

  /* ---------- 1. Spring engine ------------------------------------------- */
  function Spring(opts) {
    opts = opts || {};
    this.zeta = opts.damping != null ? opts.damping : 1;
    this.omega = (2 * Math.PI) / (opts.response != null ? opts.response : 0.4);
    this.value = opts.value || 0;
    this.target = this.value;
    this.velocity = 0;
    this.onUpdate = opts.onUpdate;
    this.onSettle = opts.onSettle;
    this._raf = null;
    this._last = 0;
  }
  Spring.prototype.params = function (d, r) { this.zeta = d; this.omega = (2 * Math.PI) / r; return this; };
  Spring.prototype._emit = function () { if (this.onUpdate) this.onUpdate(this.value, this.velocity); };
  Spring.prototype.setTarget = function (t) { this.target = t; this._run(); };
  Spring.prototype.setVelocity = function (v) { this.velocity = v; this._run(); };
  Spring.prototype.stop = function () { if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; } };
  Spring.prototype._run = function () {
    if (this._raf) return;
    var self = this;
    this._last = 0;
    this._raf = requestAnimationFrame(function tick(now) {
      if (!self._last) self._last = now;
      var dt = (now - self._last) / 1000; self._last = now;
      if (dt > 0.064) dt = 0.064;                       // clamp tab-switch gaps
      var steps = Math.max(1, Math.ceil(dt / (1 / 240)));
      var h = dt / steps, k = self.omega * self.omega, c = 2 * self.zeta * self.omega, i;
      for (i = 0; i < steps; i++) {
        var a = -k * (self.value - self.target) - c * self.velocity;
        self.velocity += a * h; self.value += self.velocity * h;
      }
      self._emit();
      if (Math.abs(self.value - self.target) < 0.15 && Math.abs(self.velocity) < 0.15) {
        self.value = self.target; self.velocity = 0; self._emit();
        self._raf = null; if (self.onSettle) self.onSettle(); return;
      }
      self._raf = requestAnimationFrame(tick);
    });
  };
  function project(v, decel) { decel = decel || 0.998; return (v / 1000) * decel / (1 - decel); }

  /* ---------- 2. Scroll reveal ------------------------------------------- */
  var REVEAL_SELECTORS = [
    // section-heading clusters
    '.problems__head', '.why__head', '.collection__head', '.testimonials__head',
    '.eyesys__head', '.benefits__head', '.botanicals__head', '.ingredients__head',
    '.steps__head', '.signals__head', '.threestep__head', '.compare__head',
    '.tests__head', '.compare-block__lede', '.faq__head', '.buy__head', '.feature__head',
    // repeating cards / items (stagger within their grid)
    '.problems__grid .pcard', '.why__grid .wcard', '.collection__row .prod',
    '.testimonials__grid .test-card', '.stats__grid .stat', '.feature__overlay .stat',
    '.benefits__grid .benefit', '.eyesys__grid .eyecard', '.botanicals__grid .bot',
    '.ingredients__grid .ing', '.steps__grid .step', '.signals__grid .signal',
    '.compare__grid .ccol', '.tests__grid .tcard', '.buy__grid .buy__plan',
    '.buy__quality__inner .qbadge',
    // two-column media/copy pairs + single blocks
    '.feature__media', '.feature__copy', '.applic__media', '.applic__copy',
    '.imgtext__media', '.imgtext__copy', '.threestep__block', '.rich__inner', '.final-cta__inner'
  ].join(',');

  var revealIO = null;
  function ensureIO() {
    if (revealIO || !('IntersectionObserver' in window)) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        revealIO.unobserve(el);
        var delay = parseInt(el.getAttribute('data-wq-delay'), 10) || 0;
        el.style.transition = 'opacity .7s var(--wq-ease-out) ' + delay + 'ms, transform .7s var(--wq-ease-out) ' + delay + 'ms';
        el.style.opacity = '1';
        el.style.transform = 'none';
        var clear = function () {
          el.style.transition = ''; el.style.opacity = ''; el.style.transform = ''; el.style.willChange = '';
          el.removeAttribute('data-wq-reveal');
          el.removeEventListener('transitionend', clear);
        };
        el.addEventListener('transitionend', clear);
        setTimeout(clear, 700 + delay + 120);           // fallback if transitionend is missed
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    return revealIO;
  }

  function initReveal(root) {
    if (reduced() || !('IntersectionObserver' in window)) return;
    var io = ensureIO();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    (root || document).querySelectorAll(REVEAL_SELECTORS).forEach(function (el) {
      if (el.dataset.wqRevealBound) return;
      if (!el.closest('.wq')) return;                    // scope to the custom layer only
      el.dataset.wqRevealBound = '1';
      // stagger: position among siblings that are also reveal targets
      var parent = el.parentNode, idx = 0;
      if (parent) {
        var sibs = parent.children, seen = 0;
        for (var i = 0; i < sibs.length; i++) {
          if (sibs[i] === el) { idx = seen; break; }
          if (sibs[i].matches && sibs[i].matches(REVEAL_SELECTORS)) seen++;
        }
      }
      el.setAttribute('data-wq-delay', Math.min(idx, 6) * 80);
      // Already in view on load? reveal instantly with no hide (no flash).
      if (el.getBoundingClientRect().top < vh * 0.92) return;
      el.setAttribute('data-wq-reveal', '');
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      io.observe(el);
    });
  }

  /* ---------- 3. Spring accordion (observes .is-open) -------------------- */
  function initAccordion(root) {
    (root || document).querySelectorAll('.wq .faq__item').forEach(function (item) {
      if (item.dataset.wqAccBound) return;
      item.dataset.wqAccBound = '1';
      var panel = item.querySelector('.faq__a');
      var inner = item.querySelector('.faq__a__in') || panel;
      if (!panel) return;

      var open = item.classList.contains('is-open');
      var sp = new Spring({
        damping: 1, response: 0.4, value: open ? inner.getBoundingClientRect().height : 0,
        onUpdate: function (v) { panel.style.height = Math.max(0, v) + 'px'; },
        onSettle: function () {
          if (item.classList.contains('is-open')) { panel.style.height = 'auto'; }
          else { panel.style.height = ''; panel.style.visibility = ''; }   // -> CSS: height:0; visibility:hidden
        }
      });

      var mo = new MutationObserver(function () {
        var nowOpen = item.classList.contains('is-open');
        if (nowOpen === open) return;
        open = nowOpen;
        if (reduced()) {                                   // instant, let CSS decide
          panel.style.height = ''; panel.style.visibility = ''; return;
        }
        panel.style.visibility = 'visible';                // keep visible through the close travel
        var target = nowOpen ? inner.getBoundingClientRect().height : 0;
        // Start value = the on-screen height so interrupts reverse smoothly. But a FRESH
        // open has no inline height, so .is-open's CSS height:auto would report the full
        // expanded height — start from 0 in that case so the panel actually expands.
        var hasInline = panel.style.height !== '' && panel.style.height !== 'auto';
        var start = hasInline ? panel.getBoundingClientRect().height
                              : (nowOpen ? 0 : inner.getBoundingClientRect().height);
        panel.style.height = start + 'px';
        sp.value = start;                                  // spring resumes from presentation value, keeps velocity
        sp.setTarget(target);
      });
      mo.observe(item, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ---------- 4. Header material / scroll-edge --------------------------- */
  /* One window listener for the life of the page; it re-queries the current
     header each tick so a theme-editor header reload can't leak listeners. */
  var headerScrollBound = false;
  function updateHeader() {
    var header = document.querySelector('.wq .site-header');
    if (header) header.classList.toggle('wq-scrolled', window.scrollY > 8);
  }
  function initHeader() {
    if (headerScrollBound) { updateHeader(); return; }
    headerScrollBound = true;
    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  }

  /* ---------- 5. Hero staged load-in ------------------------------------- */
  /* Now pure CSS (wisequest-refine.css @keyframes wq-hero-in) — no JS needed. */

  /* ---------- 6. Cart drawer drag-to-dismiss ----------------------------- */
  function initCartDrawer() {
    var cart = document.querySelector('cart-drawer');
    if (!cart || cart.dataset.wqDragBound) return;
    cart.dataset.wqDragBound = '1';                         // <cart-drawer> survives renderContents; bind ONCE here

    // .drawer__inner / .drawer__header / .drawer__overlay are replaced on every cart
    // re-render, so resolve them fresh at drag start via delegation on <cart-drawer>.
    var dragging = false, captured = false, ptrId = null, startX = 0, startY = 0, axis = null, hist = [];
    var inner = null, overlay = null;
    function width() { return inner ? (inner.getBoundingClientRect().width || 400) : 400; }
    function setX(x) {
      x = Math.max(0, x);                                    // never past the docked edge
      if (inner) inner.style.transform = 'translateX(' + x + 'px)';
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - x / width()));
    }
    function endDrag(commitClose) {
      if (inner) { inner.classList.remove('wq-dragging'); inner.style.transition = ''; inner.style.transform = ''; }
      if (overlay) overlay.style.opacity = '';
      if (commitClose && typeof cart.close === 'function') cart.close();  // -> CSS drives translateX to (100%)
    }

    cart.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      if (reduced()) return;                                 // no drag under reduced-motion
      var hdr = e.target.closest && e.target.closest('.drawer__header');
      if (!hdr || !cart.contains(hdr)) return;               // only the non-scrolling grab zone starts a drag
      inner = cart.querySelector('.drawer__inner');
      overlay = cart.querySelector('.drawer__overlay');
      if (!inner) return;
      dragging = true; axis = null; captured = false; ptrId = e.pointerId;
      startX = e.clientX; startY = e.clientY;
      hist = [{ x: startX, t: e.timeStamp }];
    });
    cart.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;    // hysteresis before committing to an axis
        axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') {
          inner.classList.add('wq-dragging'); inner.style.transition = 'none';
          try { cart.setPointerCapture(e.pointerId); captured = true; } catch (err) {} // capture only once we own it
        }
      }
      if (axis !== 'x') return;
      e.preventDefault();
      setX(dx);
      hist.push({ x: e.clientX, t: e.timeStamp }); if (hist.length > 6) hist.shift();
    });
    function release(e) {
      if (!dragging) return;
      dragging = false;
      if (captured) { try { cart.releasePointerCapture(ptrId); } catch (err) {} captured = false; }
      if (axis !== 'x') { axis = null; return; }             // was a tap / vertical — let the click through
      axis = null;
      var last = hist[hist.length - 1], first = hist[0];
      var dtv = (last.t - first.t) || 16, vel = (last.x - first.x) / dtv * 1000; // px/s at release
      var curX = Math.max(0, e.clientX - startX);
      var projected = curX + project(vel);                   // momentum projection (§6)
      var close;
      if (vel > 350) close = true;                           // decide by velocity sign first (§ reverse-vs-commit)
      else if (vel < -350) close = false;
      else close = projected > width() / 2;
      endDrag(close);
    }
    cart.addEventListener('pointerup', release);
    cart.addEventListener('pointercancel', release);
  }

  /* ---------- 7. Cart-count bump (observe the badge's number) ------------ */
  /* Motion stays in this layer: rather than editing wisequest.js's
     refreshCartCount(), watch the badge's textContent (set via el.textContent,
     a childList mutation) and pulse only when the count INCREASES. */
  function initCartCount(root) {
    if (!('MutationObserver' in window)) return;
    (root || document).querySelectorAll('.wq [data-wq-cart-count]').forEach(function (el) {
      if (el.dataset.wqCountBound) return;
      el.dataset.wqCountBound = '1';
      var prev = parseInt(el.textContent, 10) || 0;
      var mo = new MutationObserver(function () {
        var now = parseInt(el.textContent, 10) || 0;
        if (now === prev) return;
        var increased = now > prev;
        prev = now;
        if (!increased || reduced()) return;       // pulse only on an add; skip under reduced-motion
        el.classList.remove('wq-bumped');
        void el.offsetWidth;                        // force reflow so the keyframe restarts on repeat adds
        el.classList.add('wq-bumped');
      });
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---------- init ------------------------------------------------------- */
  function initAll(root) {
    initReveal(root);
    initAccordion(root);
    initHeader();
    initCartDrawer();
    initCartCount(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  // Theme editor re-renders a section
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
